// ==============================================
// File: src/lib/localFolder.js
// Helpers for reading/writing JSON/text in a chosen
// folder via the File System Access API.
// Adds: permission helpers, safe write, list utilities.
// ==============================================

/** Ensure the File System Access API is available */
function ensureFS() {
  if (!("showDirectoryPicker" in window)) {
    throw new Error(
      "This browser doesn't support folder access. Try Chrome/Edge desktop."
    );
  }
}

/** Ask user to pick a writable directory */
export async function pickWritableDirectory(options = {}) {
  ensureFS();
  // You can pass { startIn: 'downloads' | 'documents' | 'desktop' } on supported browsers
  const handle = await window.showDirectoryPicker(options);
  const ok = await verifyWrite(handle);
  if (!ok) throw new Error("Selected folder is not writable.");
  return handle;
}

/** Request permission if needed */
export async function ensurePermission(dirHandle, mode = "readwrite") {
  if (!dirHandle) return false;
  try {
    const query = await dirHandle.queryPermission({ mode });
    if (query === "granted") return true;
    const res = await dirHandle.requestPermission({ mode });
    return res === "granted";
  } catch {
    return false;
  }
}

/** Quick test that we can create + delete a file */
export async function verifyWrite(dirHandle) {
  try {
    const granted = await ensurePermission(dirHandle, "readwrite");
    if (!granted) return false;

    const fileHandle = await dirHandle.getFileHandle(".verify.tmp", { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write("ok");
    await writable.close();
    await dirHandle.removeEntry(".verify.tmp");
    return true;
  } catch (err) {
    console.warn("[localFolder] verifyWrite failed:", err);
    return false;
  }
}

/** Ensure subdirectories exist and return the deepest DirectoryHandle */
export async function ensureSubdirs(dirHandle, subdirs = []) {
  let folder = dirHandle;
  for (const part of subdirs) {
    folder = await folder.getDirectoryHandle(part, { create: true });
  }
  return folder;
}

/** Write JSON (pretty) */
export async function writeJson(dirHandle, subdirs = [], fileName, data) {
  const folder = await ensureSubdirs(dirHandle, subdirs);
  const fileHandle = await folder.getFileHandle(fileName, { create: true });

  // Best-effort "atomic": write to temp then replace content
  const tmpHandle = await folder.getFileHandle(fileName + ".tmp", { create: true });
  let w = await tmpHandle.createWritable();
  await w.write(JSON.stringify(data, null, 2));
  await w.close();

  // Overwrite target
  w = await fileHandle.createWritable();
  const tmpFile = await tmpHandle.getFile();
  await w.write(await tmpFile.text());
  await w.close();

  // Clean temp
  await folder.removeEntry(fileName + ".tmp").catch(() => {});
  return true;
}

/** Read JSON */
export async function readJson(dirHandle, subdirs = [], fileName) {
  const folder = await ensureSubdirs(dirHandle, subdirs);
  const fileHandle = await folder.getFileHandle(fileName);
  const file = await fileHandle.getFile();
  const text = await file.text();
  return JSON.parse(text);
}

/** Safe read that returns null if missing */
export async function readJsonOrNull(dirHandle, subdirs = [], fileName) {
  try {
    return await readJson(dirHandle, subdirs, fileName);
  } catch {
    return null;
  }
}

/** Write plain text */
export async function writeText(dirHandle, subdirs = [], fileName, text) {
  const folder = await ensureSubdirs(dirHandle, subdirs);
  const fileHandle = await folder.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(String(text ?? ""));
  await writable.close();
  return true;
}

/** Read plain text */
export async function readText(dirHandle, subdirs = [], fileName) {
  const folder = await ensureSubdirs(dirHandle, subdirs);
  const fileHandle = await folder.getFileHandle(fileName);
  const file = await fileHandle.getFile();
  return file.text();
}

/** List files in a (sub)folder (non-recursive) */
export async function listFiles(dirHandle, subdirs = []) {
  const folder = await ensureSubdirs(dirHandle, subdirs);
  const out = [];
  // @ts-ignore: for-await supported on DirectoryHandle
  for await (const [name, handle] of folder.entries()) {
    out.push({ name, kind: handle.kind });
  }
  return out;
}

/** Write a Blob (binary file) */
export async function writeBlob(dirHandle, subdirs = [], fileName, blob) {
  const folder = await ensureSubdirs(dirHandle, subdirs);
  const fileHandle = await folder.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
  return true;
}

/** Read a Blob (binary file) */
export async function readBlob(dirHandle, subdirs = [], fileName) {
  const folder = await ensureSubdirs(dirHandle, subdirs);
  const fileHandle = await folder.getFileHandle(fileName);
  const file = await fileHandle.getFile();
  return file;
}

/** Delete a file if it exists (returns true if deleted) */
export async function deleteFile(dirHandle, subdirs = [], fileName) {
  try {
    const folder = await ensureSubdirs(dirHandle, subdirs);
    await folder.removeEntry(fileName);
    return true;
  } catch {
    return false;
  }
}

/* Back-compat aliases */
export const writeJSON = writeJson;
export const readJSON = readJson;
export const verifyLocalWrite = verifyWrite;
